import React, { useState } from 'react';
import { ArrowLeft, Lock, BookOpen, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BuyFullJourneyGate = ({ onSkip, onBack, progress = 0, showReturnMessage = false, deviceId, onCheckoutReturn }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleUnlock = async () => {
    // Start real Stripe checkout
    setIsLoading(true);
    setError(null);
    
    try {
      // Get the frontend origin URL for Stripe redirects
      const originUrl = window.location.origin;
      
      const response = await axios.post(`${API}/start-checkout`, {
        deviceId: deviceId,
        originUrl: originUrl
      });
      
      if (response.data.success && response.data.url) {
        // Redirect to real Stripe checkout
        window.location.href = response.data.url;
      } else {
        setError('Failed to start checkout. Please try again.');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      const errorMessage = err.response?.data?.detail || 'Unable to start checkout. Please try again later.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white pb-20">
      {/* Header */}
      <div className="bg-white border-b" style={{ borderBottomColor: '#E5E7EB' }}>
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={onBack}
              className="flex items-center gap-2 hover:opacity-70 transition-opacity"
              style={{ color: '#0B1221' }}
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back</span>
            </button>
            <span className="text-sm font-semibold tracking-wide">
              <span style={{ color: '#001F3F' }}>VINdicated.</span>
              <span style={{ color: '#FF4136' }}>ai</span>
            </span>
          </div>
          
          {/* Progress Bar */}
          <div className="mb-3">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="font-medium" style={{ color: '#111827' }}>Progress</span>
              <span className="font-bold" style={{ color: '#1D4ED8' }}>{progress}%</span>
            </div>
            <div className="w-full h-2 rounded-full overflow-hidden" style={{ backgroundColor: '#E5E7EB' }}>
              <div 
                className="h-full transition-all duration-500 ease-out"
                style={{ 
                  width: `${progress}%`,
                  backgroundColor: '#1D4ED8'
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Return message if user tried to skip ahead */}
        {showReturnMessage && (
          <div 
            className="p-4 rounded-xl text-sm"
            style={{ 
              backgroundColor: 'rgba(142, 68, 173, 0.08)',
              border: '1px solid rgba(142, 68, 173, 0.2)',
              color: '#5B2C6F'
            }}
          >
            To keep going with the guided journey and unlock deal scans, you need to unlock access first.
          </div>
        )}

        {/* Error message */}
        {error && (
          <div 
            className="p-4 rounded-xl text-sm"
            style={{ 
              backgroundColor: 'rgba(239, 68, 68, 0.08)',
              border: '1px solid rgba(239, 68, 68, 0.2)',
              color: '#DC2626'
            }}
          >
            {error}
          </div>
        )}

        {/* Main Card */}
        <div 
          className="bg-white rounded-2xl shadow-md p-5 space-y-5"
          style={{ border: '1px solid #E5E7EB' }}
        >
          {/* Heading and Price */}
          <div className="space-y-3">
            <h1 
              className="text-xl font-extrabold tracking-tight leading-tight"
              style={{ color: '#001F3F' }}
            >
              Unlock the full VINdicated journey and deal scans
            </h1>
            <p className="text-sm leading-relaxed" style={{ color: '#4B5563' }}>
              You have already done some of the smartest work. Unlock the rest of the guided journey and deal scan so you can finish with confidence.
            </p>
            
            {/* Price Block */}
            <div className="pt-2">
              <div className="text-3xl font-extrabold" style={{ color: '#001F3F' }}>
                $49.99
              </div>
              <div className="text-sm" style={{ color: '#6B7280' }}>
                One time purchase for this car buying journey
              </div>
            </div>
          </div>

          {/* Divider */}
          <div style={{ borderTop: '1px solid #E5E7EB' }} />

          {/* What you unlock */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Lock className="w-5 h-5" style={{ color: '#001F3F' }} />
              <h2 className="text-base font-semibold" style={{ color: '#001F3F' }}>
                What you unlock
              </h2>
            </div>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-sm" style={{ color: '#374151' }}>
                <span 
                  className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0"
                  style={{ backgroundColor: '#001F3F' }}
                />
                <span>Full guided Buy journey from today until you sign, without piecing it together alone.</span>
              </li>
              <li className="flex items-start gap-3 text-sm" style={{ color: '#374151' }}>
                <span 
                  className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0"
                  style={{ backgroundColor: '#001F3F' }}
                />
                <span>Unlimited contract scans for this purchase for ninety days. We help you review your buyer order, finance contract, and add on forms.</span>
              </li>
              <li className="flex items-start gap-3 text-sm" style={{ color: '#374151' }}>
                <span 
                  className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0"
                  style={{ backgroundColor: '#001F3F' }}
                />
                <span>Plain language explanations for unusual fees and terms so you can ask calmer and smarter questions.</span>
              </li>
            </ul>
          </div>

          {/* Divider */}
          <div style={{ borderTop: '1px solid #E5E7EB' }} />

          {/* What stays free */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <BookOpen className="w-5 h-5" style={{ color: '#001F3F' }} />
              <h2 className="text-base font-semibold" style={{ color: '#001F3F' }}>
                What is always free
              </h2>
            </div>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-sm" style={{ color: '#374151' }}>
                <span 
                  className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0"
                  style={{ backgroundColor: '#6B7280' }}
                />
                <span>All Learn articles and education inside VINdicated.</span>
              </li>
              <li className="flex items-start gap-3 text-sm" style={{ color: '#374151' }}>
                <span 
                  className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0"
                  style={{ backgroundColor: '#6B7280' }}
                />
                <span>All tools like the APR savings calculator, trade in tools, and car comparison.</span>
              </li>
              <li className="flex items-start gap-3 text-sm" style={{ color: '#374151' }}>
                <span 
                  className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0"
                  style={{ backgroundColor: '#6B7280' }}
                />
                <span>You can still use these at any time even if you do not unlock the full journey.</span>
              </li>
            </ul>
          </div>

          {/* Divider */}
          <div style={{ borderTop: '1px solid #E5E7EB' }} />

          {/* Trust and safety */}
          <div className="space-y-3">
            <h2 className="text-base font-semibold" style={{ color: '#001F3F' }}>
              Trust and safety
            </h2>
            <p className="text-sm leading-relaxed" style={{ color: '#4B5563' }}>
              VINdicated is education only, not financial or legal advice. We do not sell your personal data. You can export and delete your data from the Settings page at any time.
            </p>
            <p className="text-sm leading-relaxed" style={{ color: '#4B5563' }}>
              If you complete the journey and scans and still feel it was not worth it, you can email our support team and request a refund.
            </p>
          </div>

          {/* Divider */}
          <div style={{ borderTop: '1px solid #E5E7EB' }} />

          {/* CTA Buttons */}
          <div className="space-y-4 pt-1">
            {/* Primary CTA */}
            <button
              onClick={handleUnlock}
              disabled={isLoading}
              className="w-full py-4 px-6 rounded-2xl font-bold text-lg transition-all shadow-md hover:shadow-lg disabled:opacity-50"
              style={{ backgroundColor: '#001F3F', color: '#FFFFFF' }}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Starting checkout...
                </span>
              ) : (
                'Unlock and continue'
              )}
            </button>

            {/* Secondary Link */}
            <button
              onClick={onSkip}
              disabled={isLoading}
              className="w-full text-sm font-medium hover:underline transition-opacity disabled:opacity-50"
              style={{ color: '#001F3F' }}
            >
              Continue without unlocking
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuyFullJourneyGate;
